/* tslint:disable:no-var-requires */
/* tslint:disable:import-name */


import getFilters, { IFilterConfig, ISearchConfig } from 'AWFGrid/Filters/Filters';
import { IFilterEditor, IFilterEditorOptions } from 'AWFGrid/Filters/Editors/IFilterEditor';
import * as i18n from 'CMS/core/i18n';
import event, { IEvent } from 'CMS/Event/Event';
import * as application from 'CMS/core/application';

import * as kendo from 'kendo/kendo.core';
import 'kendo/kendo.multiselect';
import { ISearchEditor, Search, ISearchEditorOptions } from './SearchEditors/ISearchEditor';

const TextSearchEditorId = "search";

export interface IFilterListOptions {
    type: string;
    config?: 'grids';
}

interface ITagCloudItem {
    id: string;
    title: string;
    clear(): void;
}

class FilterItem {
    public id: string;
    public config: IFilterConfig;
    public filterEditor: IFilterEditor;
}

export default class FiltersList {
    public filtersChanged: IEvent<kendo.data.DataSourceFilter> = new event<kendo.data.DataSourceFilter>();
    public searchChanged: IEvent<Search> = new event<Search>();
    private filterItems: FilterItem[] = [];
    private search?: ISearchEditor;
    private ignoreFilterChangedEvents: boolean = false;

    // Tags Cloud
    private $tagsCloudContainer: JQuery;
    private $tagsCloud: kendo.ui.MultiSelect;
    private tagsCloudDataSource = new kendo.data.DataSource([]);
    private previousTagCloudIds: string[] = [];


    /**
     * Initialize the filters list and append it to the given element
     * @param element
     * @param options
     */
    public async initialize(element: HTMLElement, options: IFilterListOptions): Promise<FiltersList> {
        const editorPromises: Promise<void>[] = [];
        const filters = await getFilters(options.type, options.config);
        const $element = $(element);

        for (let i = 0; filters != null && i < filters.length; i++) {
            const filter = filters[i];

            // Check permissions
            if (filter.permission === undefined || filter.permission == null || filter.permission === '' || this.currentUserHasPermissionByFullName(filter.permission)) {
                const $editorContainer = $('<div />');
                $editorContainer.appendTo($element);
                editorPromises.push(this.initializeEditor(i.toString(), filter, $editorContainer));                
            }
        }

        // Clear Button
        var $clearButtonContainer = $('<div class="filter-editor"/>');
        var $clearButton = $('<input type="button" class="form-control btn-clear-filters btn btn-link capitalize-inline" value="' + i18n.getMessage('filters.clearAll') + '"></input>');
        $clearButton.on('click', e => { this.onClearButtonClicked(e); });
        $clearButtonContainer.appendTo($element);
        $clearButton.appendTo($clearButtonContainer);


        /* Resolve once all filter editors are initialized */
        await Promise.all(editorPromises);
        this.onFiltersChanged();
        if (this.search) { this.onSearchChanged(); }
        return this;
    }

    private async initializeEditor(id: string, config: IFilterConfig | ISearchConfig, $container: JQuery): Promise<void> {
        // Create label DOM element
        const $label = $('<div class="filter-label hidden capitalize" />');
        $label.appendTo($container);
        $label.text(config.title);

        // Create editor DOM element
        const $filter = $('<div class="filter-editor hidden" />');
        $filter.appendTo($container);

        const isEnabled = await (('search' in config) ? this.initializeSearchEditor(config, $container) : this.initializeFilterEditor(id, config, $container));
        if (isEnabled) {
            $label.removeClass('hidden');
            $filter.removeClass('hidden');
        }        
    }

    private async initializeFilterEditor(id: string, config: IFilterConfig, $container: JQuery<HTMLElement>): Promise<boolean> {
        const editor = config.editor;
        let editorPath = editor.id;
        if (editor.id.indexOf('/') === -1) {
            editorPath = 'AWFGrid/Filters/Editors/' + editor.id + 'FilterEditor';
        }

        const mod = await import(editorPath);

        // Create FilterEditor
        const Editor = mod.default;
        const filterEditor: IFilterEditor = new Editor();

        //  Create FilterItem
        const filterItem = new FilterItem();
        filterItem.id = id;
        filterItem.config = config;
        filterItem.filterEditor = filterEditor;

        this.filterItems.push(filterItem);       

        const filterEditorOptions: IFilterEditorOptions = {
            operator: <string>config.operator,
            field: <string>config.field,
            options: editor.options, title: config.title
        };

        if (config.valueMapper) {
            const ValueMapper = await import("./ValueMappers/ValueMapper");
            filterEditorOptions.valueMapper = await ValueMapper.getMapper(config.valueMapper);
        }

        const isEnabled = await filterEditor.initialize($container[0], filterEditorOptions);        
        filterEditor.filtersChanged.on(data => { this.onFilterEditorChanged(filterItem, data); });
        return isEnabled;
    }

    private async initializeSearchEditor(config: ISearchConfig, $container: JQuery): Promise<boolean> {
        const search = config.search;
        let editorPath: string = search;
        if (search.indexOf('/') === -1) {
            editorPath = 'AWFGrid/Filters/SearchEditors/' + search + 'SearchEditor';
        }

        const mod = await import(editorPath);

        // Create SearchEditor
        const Editor = mod.default;
        const editorOptions: ISearchEditorOptions = { title: config.title };
        if (config.valueMapper) {
            const ValueMapper = await import("./ValueMappers/ValueMapper");
            editorOptions.valueMapper = await ValueMapper.getMapper(config.valueMapper);
        }
        this.search = new Editor(editorOptions);        

        /* Initialize & attach search changed handler */
        const isEnabled = this.search.initialize($container[0], editorOptions);
        this.search.searchChanged.on(_ => { this.onSearchChanged(); });
        return isEnabled;
    }

    /**
     * Initialize tags cloud
     * @param element
     */
    public initializeTagsCloud(element: HTMLElement): void {
        this.$tagsCloudContainer = $(element);
        this.$tagsCloudContainer.addClass('tags-cloud');
        this.$tagsCloudContainer.kendoMultiSelect({
            dataSource: this.tagsCloudDataSource,
            clearButton: false,
            tagTemplate: '#: title #',
            dataValueField: 'id',
            change: (e) => {
                this.onTagCloudsChanged();
            }, open: (e) => {
                e.preventDefault();
            }
        });

        this.$tagsCloudContainer.parent().on('click', 'li.k-button', (e) => {
            $(e.currentTarget).find('.k-i-close').click();
        });
        this.$tagsCloud = this.$tagsCloudContainer.data('kendoMultiSelect');
        var $input = this.$tagsCloudContainer.closest('.k-multiselect').find('input');
        $input.attr('disabled', 'disabled');
        $input.on('keypress', e => {
            e.preventDefault();
        });

        this.redrawTagClouds();
    }

    /**
     * Get the filter item with the given id
     * @param id
     */
    private getFilterItemById(id: string): FilterItem {
        for (let i = 0; i < this.filterItems.length; i++) {
            if (this.filterItems[i].id === id) {
                return this.filterItems[i];
            }
        }
        return null;
    }

    private onFilterEditorChanged(filterItem: FilterItem, filters: kendo.data.DataSourceFilter): void {
        this.onFiltersChanged(); // Raise filterchanged event
    }    

    private onClearButtonClicked(e: JQuery.Event): void {
        e.stopPropagation();
        this.clearFilters();
    }

    public dataBound(data: any[]): void {
        /* Data Bound on each filter editor */
        for (let i = 0; i < this.filterItems.length; i++) {
            this.filterItems[i].filterEditor.dataBound(data);
        }

        /* Data bound search editor */
        this.search?.dataBound(data);
    }    

    /**
     * Clears filters and search
     */
    public clearFilters(): void {
        try {
            this.ignoreFilterChangedEvents = true;
            for (let i = 0; i < this.filterItems.length; i++) {
                this.filterItems[i].filterEditor.clear();
            }
            this.search?.clear();
        } finally {
            this.ignoreFilterChangedEvents = false;
            this.onFiltersChanged();            
        }
    }

    public reload(): void {    
        try {
            this.ignoreFilterChangedEvents = true;
            /* Reload filters */
            for (let i = 0; i < this.filterItems.length; i++) {
                this.filterItems[i].filterEditor.clear();
                this.filterItems[i].filterEditor.reload();
            }

            /* Reload search */
            this.search?.clear();
            this.search?.reload();

            /* Redraw tags */
            this.redrawTagClouds();
        } finally {            
            this.ignoreFilterChangedEvents = false;            
        }
    }

    private onTagCloudsChanged(): void {
        var previousTagCloudIds = this.previousTagCloudIds;
        var currentTagCloudIds = <string[]>this.$tagsCloud.value();

        // Loop over previous items to find removed item
        var deletedTagCloud: ITagCloudItem = null;
        if (previousTagCloudIds) {
            for (let i = 0; i < previousTagCloudIds.length; i++) {
                var previousActiveFilterId = previousTagCloudIds[i];
                if (currentTagCloudIds.indexOf(previousActiveFilterId) < 0) {
                    // This item is no longer in the current selection => deleted
                    var tagClouds = this.$tagsCloud.dataSource.data();
                    for (var j = 0; j < tagClouds.length; j++) {
                        var tagCloud = tagClouds[j];
                        if (tagCloud.id === previousActiveFilterId) {
                            deletedTagCloud = tagCloud;
                            break;
                        }
                    }
                    break;
                }
            }
        }

        if (deletedTagCloud != null) {
            this.onTagCloudDeleted(deletedTagCloud);
        }
    }

    private onTagCloudDeleted(tagCloud: ITagCloudItem): void {
        tagCloud.clear();
    }

    /**
     * Redraw tag clouds
     */
    private redrawTagClouds(): void {
        if (this.$tagsCloud != null) {
            var tagClouds: ITagCloudItem[] = [];
            var tagCloudIds: string[] = [];

            // Loop over all filter items
            for (let i = 0; i < this.filterItems.length; i++) {
                var filterItem = this.filterItems[i];
                const filterEditor = filterItem.filterEditor;
                var subFilters = filterEditor.getSubFilters();
                if (subFilters != null) {
                    for (var j = 0; j < subFilters.length; j++) {
                        const subFilter = subFilters[j];
                        var id = filterItem.id + ':' + subFilter.id;
                        tagClouds.push({
                            id: id,
                            title: subFilter.tagText,
                            clear: () => {
                                filterEditor.clearSubFilter(subFilter);
                            }
                        });
                        tagCloudIds.push(id);
                    }
                }
            }

            const searchValue = this.search?.getSearch()?.value;
            if (searchValue) {
                tagClouds.push({
                    id: "$search",
                    title: searchValue,
                    clear: () => {
                        this.search.clear();
                    }
                });
                tagCloudIds.push("$search");
            }

            // Clear previous tags cloud value
            this.previousTagCloudIds = null;

            // Clear DataSource
            var dataSource = this.$tagsCloud.dataSource;
            var raw = dataSource.data();
            var length = raw.length;
            var item: any;
            for (let i = length - 1; i >= 0; i--) {
                item = raw[i];
                dataSource.remove(item);
            }

            // Add all tag clouds to datasource
            dataSource.data(tagClouds);

            // Set tags cloud value
            this.$tagsCloud.value(tagCloudIds);

            // Set previous tags cloud value
            this.previousTagCloudIds = tagCloudIds;

            if (tagClouds.length === 0) {
                this.$tagsCloud.wrapper.hide();
                this.$tagsCloud.wrapper.removeClass('has-tags');
            } else {
                this.$tagsCloud.wrapper.show();
                this.$tagsCloud.wrapper.addClass('has-tags');
            }
        }
    }

    private currentUserHasPermissionByFullName(permission: string): boolean {
        if (permission.toLowerCase() === 'loggedin' && application.getCurrentUser().loggedIn)
            return true;

        var userPermissions = application.getCurrentUser().permissions;
        for (let i = 0; i < userPermissions.length; i++) {
            if (userPermissions[i].toLowerCase() === permission.toLowerCase()) {
                return true;
            }
        }
        return false;
    }

    public hasFilters(): boolean {
        for (let i = 0; i < this.filterItems.length; i++) {
            var filter = this.filterItems[i].filterEditor.getFilter();
            if (filter != null) { return true; }
        }

        return false;
    }
    public getFilters(): kendo.data.DataSourceFilters {
        var data: kendo.data.DataSourceFilters = {
            logic: 'and',
            filters: []
        };

        var activeFilterIds: string[] = [];

        // Merge all filters
        for (let i = 0; i < this.filterItems.length; i++) {
            var filterItem = this.filterItems[i];
            var filter = filterItem.filterEditor.getFilter();
            if (filter != null) {
                activeFilterIds.push(filterItem.id);
                data.filters.push(filter);
            }
        }


        return data;
    }
    public getSearch(): Search {
        return this.search?.getSearch();
    }

    private onFiltersChanged(): void {
        if (!this.ignoreFilterChangedEvents) {
            var data = this.getFilters();

            // Update tag clouds
            this.redrawTagClouds();

            // Raise event
            (<event<kendo.data.DataSourceFilter>>this.filtersChanged).trigger(data);
        }
    }

    private onSearchChanged(): void {
        if (!this.ignoreFilterChangedEvents) {
            // Update tag clouds
            this.redrawTagClouds();

            // Raise event            
            (<event<Search>>this.searchChanged).trigger(this.search?.getSearch());
        }
    }
}